import { useState, useRef, useMemo, memo, useCallback } from 'react';
import { VoteHistoryPoint } from '../types/vote';
import { SkeletonLoader } from './SkeletonLoader';
import { BarChart3 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '../utils/cn';
import { SectionHeader } from './ui/SectionHeader';
import { EmptyState } from './ui/EmptyState';

interface VoteChartProps {
  data: VoteHistoryPoint[];
  height?: number;
  isLoading?: boolean;
  title?: string;
  /** Any CSS colour (a `var(--…)` token is preferred over a literal). */
  accentColor?: string;
  emptyMessage?: string;
  /** Render as an inset `.subcard` instead of a `.glass-card` (use inside another card or a modal). */
  bare?: boolean;
}

const PADDING = { top: 24, right: 24, bottom: 48, left: 56 };
const VIEWBOX_WIDTH = 900;

/** Safely extract the vote count from a history data point */
const safeVoteCount = (d: VoteHistoryPoint): number => {
  const v = d.vote_count;
  return typeof v === 'number' && isFinite(v) ? v : 0;
};

/** Safely extract the date/time string from a history data point (API uses 'period' field) */
const safeDateStr = (d: VoteHistoryPoint): string => {
  // The API returns dates like "2026-02-18 00:00:00" or "2026-02-18 14:00:00"
  // Convert space-separated to ISO format for parseISO
  return (d.period || '').replace(' ', 'T');
};

/** Detect if data is hourly granularity (has non-midnight times) */
const isHourlyData = (data: VoteHistoryPoint[]): boolean => {
  if (data.length < 2) return false;
  // If any entry has a non-00:00:00 time, or if multiple entries share the same date, it's hourly
  const dates = new Set(data.map(d => (d.period || '').split(' ')[0]));
  return dates.size < data.length;
};

const ChartSkeleton = ({ height, bare }: { height: number; bare?: boolean }) => (
  <div className={cn(bare ? 'subcard p-4' : 'glass-card p-4 md:p-5')} aria-busy="true">
    <div className="flex items-center justify-between mb-4">
      <SkeletonLoader className="h-4 w-40" />
      <SkeletonLoader className="h-3 w-12" />
    </div>
    <div className="skeleton w-full rounded-lg" style={{ height }} aria-hidden="true" />
  </div>
);

export const VoteChart = memo(({ data, height = 300, isLoading, title, accentColor = 'var(--accent-300)', emptyMessage = 'No vote data available for this period.', bare = false }: VoteChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const plotWidth = VIEWBOX_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = height - PADDING.top - PADDING.bottom;
  // Gradient ids must be unique per colour and valid as an id (strip anything non-alphanumeric).
  const gradientId = `voteChartGradient-${accentColor.replace(/[^a-zA-Z0-9]/g, '')}`;

  const { points, yTicks, xLabels } = useMemo(() => {
    if (!data || data.length === 0) {
      return { points: [], yTicks: [], xLabels: [] };
    }

    const votes = data.map(safeVoteCount);
    const maxV = Math.max(...votes, 1);
    // Round up to a nice number for the y-axis
    const magnitude = maxV >= 1 ? Math.pow(10, Math.floor(Math.log10(maxV))) : 1;
    const niceMax = Math.ceil(maxV / magnitude) * magnitude;
    const actualMax = Math.max(niceMax, 5);

    const pts = data.map((d, i) => ({
      x: PADDING.left + (data.length === 1 ? plotWidth / 2 : (i / (data.length - 1)) * plotWidth),
      y: PADDING.top + plotHeight - (safeVoteCount(d) / actualMax) * plotHeight,
      date: safeDateStr(d),
      votes: safeVoteCount(d),
    }));

    // Y-axis ticks (5 ticks)
    const tickCount = 5;
    const yT = Array.from({ length: tickCount + 1 }, (_, i) => {
      const value = Math.round((actualMax / tickCount) * i);
      const y = PADDING.top + plotHeight - (value / actualMax) * plotHeight;
      return { value, y };
    });

    // X-axis labels — sample to avoid overcrowding
    const maxLabels = 12;
    const step = Math.max(1, Math.ceil(data.length / maxLabels));
    const xL = data
      .map((d, i) => ({ index: i, date: safeDateStr(d) }))
      .filter((_, i) => i % step === 0 || i === data.length - 1);

    return { points: pts, yTicks: yT, xLabels: xL };
  }, [data, plotWidth, plotHeight]);

  const linePath = useMemo(() => {
    if (points.length === 0) return '';
    if (points.length === 1) return '';
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  }, [points]);

  const areaPath = useMemo(() => {
    if (points.length < 2) return '';
    const bottom = PADDING.top + plotHeight;
    return linePath + ` L${points[points.length - 1].x.toFixed(1)},${bottom} L${points[0].x.toFixed(1)},${bottom} Z`;
  }, [linePath, points, plotHeight]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * VIEWBOX_WIDTH;

    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < points.length; i++) {
      const dist = Math.abs(points[i].x - svgX);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    }
    setHoveredIndex(closest);
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, [points]);

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  const hourly = useMemo(() => isHourlyData(data), [data]);

  const formatDate = (dateStr: string) => {
    try {
      if (!dateStr) return '';
      const d = parseISO(dateStr);
      return hourly ? format(d, 'MMM d, HH:mm') : format(d, 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatShortDate = (dateStr: string) => {
    try {
      if (!dateStr) return '';
      const d = parseISO(dateStr);
      return hourly ? format(d, 'HH:mm') : format(d, 'MMM d');
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return <ChartSkeleton height={height} bare={bare} />;
  }

  return (
    <div ref={containerRef} className={cn('relative', bare ? 'subcard p-4' : 'glass-card p-4 md:p-5')}>
      {title && (
        <SectionHeader
          title={title}
          icon={BarChart3}
          actions={data.length > 0 ? (
            <span className="text-xs text-glass-400 tabular-nums">
              {data.length} {hourly ? 'hour' : 'day'}{data.length !== 1 ? 's' : ''}
            </span>
          ) : undefined}
        />
      )}

      {data.length === 0 ? (
        <EmptyState bare compact icon={BarChart3} title="No vote data" description={emptyMessage} />
      ) : (
        <div className="relative">
          <svg
            viewBox={`0 0 ${VIEWBOX_WIDTH} ${height}`}
            className="w-full select-none"
            style={{ height }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            role="img"
            aria-label={title || 'Vote history chart'}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" style={{ stopColor: accentColor }} stopOpacity="0.28" />
                <stop offset="100%" style={{ stopColor: accentColor }} stopOpacity="0.02" />
              </linearGradient>
            </defs>

            {/* Horizontal grid lines */}
            {yTicks.map((tick, i) => (
              <g key={i}>
                <line
                  x1={PADDING.left}
                  y1={tick.y}
                  x2={VIEWBOX_WIDTH - PADDING.right}
                  y2={tick.y}
                  className="text-glass-200"
                  stroke="currentColor"
                  strokeOpacity="0.5"
                  strokeDasharray="4 4"
                />
                <text
                  x={PADDING.left - 12}
                  y={tick.y + 4}
                  textAnchor="end"
                  className="text-glass-400"
                  fill="currentColor"
                  fontSize="12"
                  fontFamily="ui-monospace, monospace"
                >
                  {tick.value.toLocaleString()}
                </text>
              </g>
            ))}

            {/* X-axis labels */}
            {xLabels.map((label) => {
              const pt = points[label.index];
              if (!pt) return null;
              return (
                <text
                  key={label.index}
                  x={pt.x}
                  y={height - 8}
                  textAnchor="middle"
                  className="text-glass-400"
                  fill="currentColor"
                  fontSize="11"
                >
                  {formatShortDate(label.date)}
                </text>
              );
            })}

            {/* Area fill */}
            {areaPath && (
              <path
                d={areaPath}
                fill={`url(#${gradientId})`}
                className="transition-opacity duration-500"
              />
            )}

            {/* Main line */}
            {linePath && (
              <path
                d={linePath}
                fill="none"
                style={{ stroke: accentColor }}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}

            {/* Single point indicator */}
            {points.length === 1 && (
              <circle
                cx={points[0].x}
                cy={points[0].y}
                r={6}
                style={{ fill: accentColor }}
              />
            )}

            {/* Hover indicator */}
            {hoveredIndex !== null && points[hoveredIndex] && (
              <>
                <line
                  x1={points[hoveredIndex].x}
                  y1={PADDING.top}
                  x2={points[hoveredIndex].x}
                  y2={PADDING.top + plotHeight}
                  style={{ stroke: accentColor }}
                  strokeOpacity="0.4"
                  strokeWidth="1"
                  strokeDasharray="4 3"
                />
                <circle
                  cx={points[hoveredIndex].x}
                  cy={points[hoveredIndex].y}
                  r={5}
                  style={{ fill: accentColor, stroke: 'var(--bg-elevated)' }}
                  strokeWidth="2"
                />
              </>
            )}
          </svg>

          {/* HTML Tooltip overlay */}
          {hoveredIndex !== null && points[hoveredIndex] && (
            <div
              className="absolute pointer-events-none z-10"
              style={{
                left: mousePos.x,
                top: mousePos.y - 70,
                transform: 'translateX(-50%)',
              }}
            >
              <div
                className="rounded-lg border border-glass-200/20 px-3 py-2 text-center animate-fade-in"
                style={{ background: 'var(--bg-elevated)', boxShadow: 'var(--shadow-popover)' }}
              >
                <div className="text-xs text-glass-400 whitespace-nowrap tabular-nums">{formatDate(points[hoveredIndex].date)}</div>
                <div className="text-base font-semibold text-white tabular-nums">{points[hoveredIndex].votes.toLocaleString()}</div>
                <div className="text-[10px] text-glass-400">votes</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

VoteChart.displayName = 'VoteChart';
